import { FormField, Select, SelectProps } from '@amzn/awsui-components-react'
import { useCallback, useEffect, useState } from 'react'
import { useAppContext } from '../../../context'
import { PROGRAM_ID_SEPARATOR, PROGRAM_SELECT_VALIDATION, ProjectType } from '../../Constant'
import useStore from '../../Store'
import BusinessEntityRefresh from '../reusable/BusinessEntityRefresh'
import { isProgramProjectActiveForYear } from '../../common/Util'
import { useNavigate, useLocation } from 'react-router-dom'
import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces'

interface CreateProjectTableProgramNameProps {
    id: string
    selectedProgram: OptionDefinition
    selectedProgramError: string
    onTeamSelectedProgramChange: (id: string, type: string, value: any) => void
    isProgramPreSelected: boolean
    selectedYear: number
    projectType: ProjectType
}

const CreateProjectTableProgramName = (props: CreateProjectTableProgramNameProps) => {
    const {
        id,
        selectedProgram,
        selectedProgramError,
        onTeamSelectedProgramChange,
        isProgramPreSelected,
        selectedYear,
        projectType,
    } = props

    const appContext = useAppContext()
    const apiClient = appContext.apiClient

    const selectBusinessEntity = useStore((state) => state.selectBusinessEntity)

    const [programOptions, setProgramOptions] = useState<SelectProps.Options | null>(null)
    const [programsLoading, setProgramsLoading] = useState<boolean>(true)
    const navigate = useNavigate()
    const location = useLocation()
    const getAllProgramsByBusinessEntity = useCallback(() => {
        apiClient
            .get(`/business-entity/${selectBusinessEntity.id}/year/${selectedYear}/programs`)
            .then((response) => {
                const allPrograms = response.data
                let options: OptionDefinition[] = []
                if (allPrograms.length) {
                    let activePrograms = allPrograms.filter((program) =>
                        isProgramProjectActiveForYear(program),
                    )
                    if (projectType === ProjectType.SERVICE_PROJECT) {
                        // service projects can only be created for true programs.
                        activePrograms = activePrograms.filter((program) => program.is_true_program)
                    }
                    options = [
                        ...activePrograms.map((program) => {
                            return {
                                label: program.program_name,
                                value: `${program.program_id}${PROGRAM_ID_SEPARATOR}${program.local_program_id}`,
                            }
                        }),
                    ]
                }
                setProgramOptions(options)
            })
            .catch((error) => console.error(error))
            .finally(() => {
                setProgramsLoading(false)
            })
    }, [selectBusinessEntity, selectedYear, projectType])

    useEffect(() => {
        if (isProgramPreSelected) {
            setProgramOptions([selectedProgram])
        }
    }, [selectedProgram])

    useEffect(() => {
        if (!isProgramPreSelected && selectedYear) {
            BusinessEntityRefresh(selectBusinessEntity.id, getAllProgramsByBusinessEntity)
            return
        }
        if (
            localStorage.getItem('selectedProgramBusinessEntity') !== selectBusinessEntity.id &&
            (location.pathname.includes('program') || location.pathname.includes('project'))
        ) {
            navigate(location.pathname)
            return
        }
        setProgramOptions([selectedProgram])
    }, [selectBusinessEntity, selectedYear])

    return (
        <FormField errorText={selectedProgramError}>
            <Select
                selectedOption={selectedProgram}
                filteringType='auto'
                onChange={({ detail }) => {
                    const errorMessage =
                        detail.selectedOption.value === '' ? PROGRAM_SELECT_VALIDATION : ''
                    onTeamSelectedProgramChange(id, 'selectedProgramError', errorMessage)
                    onTeamSelectedProgramChange(id, 'selectedProgram', detail.selectedOption)
                }}
                onBlur={() => {
                    if (selectedProgram?.value === '') {
                        onTeamSelectedProgramChange(
                            id,
                            'selectedProgramError',
                            PROGRAM_SELECT_VALIDATION,
                        )
                    }
                }}
                options={programOptions ?? []}
                expandToViewport
                statusType={
                    programsLoading ? 'loading' : programOptions === null ? 'error' : 'finished'
                }
                errorText={`Error fetching programs for year ${selectedYear}`}
                loadingText={`Loading programs for year ${selectedYear}`}
                placeholder='Select Program'
                empty={`No Programs found for year ${selectedYear}`}
                readOnly={isProgramPreSelected}
            />
        </FormField>
    )
}

export default CreateProjectTableProgramName
